import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowsLeftRight, Coins, CurrencyDollar } from "@phosphor-icons/react";

/**
 * Two-way converter between points and USD cash balance.
 * Uses admin-set point_price from /settings.
 */
export default function ConvertPanel() {
  const { user, refresh } = useAuth();
  const [price, setPrice] = useState(0.05);
  const [dir, setDir] = useState("points_to_cash"); // or cash_to_points
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setPrice(data.point_price ?? 0.05));
  }, []);

  const numeric = parseFloat(amount) || 0;
  const preview =
    dir === "points_to_cash"
      ? { label: "You get", value: `$${(Math.floor(numeric) * price).toFixed(4)}` }
      : { label: "You get", value: `${Math.floor(numeric / price)} cr` };

  const submit = async () => {
    if (!numeric || numeric <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/wallet/convert", { direction: dir, amount: numeric });
      toast.success("Converted");
      setAmount("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to convert");
    } finally {
      setSubmitting(false);
    }
  };

  const swap = () => {
    setDir((d) => (d === "points_to_cash" ? "cash_to_points" : "points_to_cash"));
    setAmount("");
  };

  const fromLabel = dir === "points_to_cash" ? "Credits to convert" : "USD to convert";
  const fromIcon = dir === "points_to_cash" ? Coins : CurrencyDollar;
  const FromIcon = fromIcon;
  const availableFrom =
    dir === "points_to_cash" ? `${user?.points ?? 0} cr available` : `$${(user?.balance || 0).toFixed(2)} available`;

  return (
    <Card className="bg-[#121212] border-white/10" data-testid="convert-panel">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <ArrowsLeftRight size={20} weight="bold" className="text-cyan-300" />
          Convert Credits {"\u2194"} cash
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 justify-between">
          <span className="text-xs text-white/50 uppercase tracking-widest font-bold">
            {dir === "points_to_cash" ? "Credits → Cash" : "Cash → Credits"}
          </span>
          <button
            data-testid="convert-swap-btn"
            onClick={swap}
            className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-white/70"
            title="Swap direction"
          >
            <ArrowsLeftRight size={18} weight="bold" />
          </button>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/50 font-bold">{fromLabel}</label>
          <div className="relative mt-1">
            <FromIcon size={16} weight="fill" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
            <Input
              data-testid="convert-amount-input"
              type="number"
              min="0"
              step={dir === "points_to_cash" ? "1" : "0.01"}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={dir === "points_to_cash" ? "e.g. 100" : "e.g. 5.00"}
              className="bg-white/5 border-white/10 text-white pl-9"
            />
          </div>
          <p className="text-[11px] text-white/40 mt-1">{availableFrom}</p>
        </div>

        <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-baseline justify-between">
          <span className="text-xs uppercase tracking-widest text-cyan-300 font-bold">{preview.label}</span>
          <span className="font-heading text-2xl font-black text-white">{preview.value}</span>
        </div>

        <Button
          data-testid="convert-submit-btn"
          onClick={submit}
          disabled={submitting || !numeric}
          className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 font-heading font-bold"
        >
          {submitting ? "Converting…" : "Convert"}
        </Button>

        <p className="text-[11px] text-white/40 text-center">
          Rate: 1 Credit = ${price.toFixed(4)} (set by admin)
        </p>
      </CardContent>
    </Card>
  );
}
